// Modal de confirmação spatial-nav friendly. Substitui window.confirm() —
// usado pra ações destrutivas (logout, hard reset, deletar perfil etc).
//
// Foco inicial vai pro botão "Cancelar" (default seguro). BACK fecha sem
// confirmar. Variant "danger" pinta o botão de confirmação em vermelho.
//
// FIX 2026-07 (armadilha de foco reportada na LG real):
//  - TRAP DE FOCO: as setas que não têm destino DENTRO do dialog eram devolvidas
//    ao Norigin (`return true`), que podia pousar o foco num botão da tela ATRÁS
//    do backdrop — o usuário ficava com o dialog aberto e o foco invisível, sem
//    conseguir voltar. Agora toda seta é consumida: o foco não escapa do dialog.
//  - RESTAURAÇÃO: ao fechar, o foco volta pro botão que abriu o dialog. Sem isso o
//    `currentFocusKey` do Norigin ficava apontando pro botão do dialog já
//    desmontado e o D-pad morria (sintoma: "depois não consigo mais selecionar o
//    Tentar de novo"). Quem decide se restaura é `shouldRestoreFocus` — confirmar
//    NÃO implica desmonte da tela de origem no dialog de AVISO (`hideCancel`),
//    onde confirmar é só dispensar.
//  - `hideCancel`: dialog de AVISO (1 botão só) — usado quando não há decisão a
//    tomar, só uma informação a confirmar (ex.: "não foi possível fechar o app").

import { useTranslation } from '@app-iptv/i18n'
import { Focusable, doesFocusableExist, getCurrentFocusKey, setFocus } from '@app-iptv/ui'
import { useEffect, useRef } from 'react'

import { useBackKey } from '../hooks/useBackKey'

import styles from './ConfirmDialog.module.css'

const CANCEL_KEY = 'confirm-dialog-cancel'
const CONFIRM_KEY = 'confirm-dialog-confirm'

/**
 * Decisão PURA (testável sem DOM/Norigin): ao desmontar, devolvo o foco pro botão
 * que abriu o dialog?
 *
 * - Dialog de DECISÃO (tem Cancelar): confirmar = seguir com a ação, e o caller
 *   costuma navegar/desmontar a tela de origem. Restaurar ali apontaria o
 *   `currentFocusKey` do Norigin pra um focusKey que não existe mais (foco
 *   fantasma) -> NÃO restaura.
 * - Dialog de AVISO (`hideCancel`): confirmar É dispensar -- não há ação nenhuma
 *   a executar e a tela de origem CONTINUA montada. Aqui o "confirmou" não
 *   implica desmonte, e pular o restore deixaria o foco pousado num lugar
 *   arbitrário. Caso real: Welcome -> "Sair" -> `window.close()` é no-op (Android
 *   TV / Fire TV / browser, onde o exit sempre cai no `exitFailed`) -> "Entendi"
 *   -> o foco tem que voltar pro botão "Sair" que abriu o aviso.
 * - Cancelar/BACK: sempre restaura (a tela de origem segue viva por definição).
 *
 * TERCEIRO CASO (2026-08-13, achado do gate de UX no "Sair do app"): existe
 * dialog de DECISÃO cujo confirmar NÃO navega e NÃO desmonta a origem — ele
 * dispara um pedido ASSÍNCRONO que pode não fazer nada. É o dialog de saída do
 * app: confirmar chama `requestExit()`, e a tela de Configurações continua
 * inteira atrás do backdrop. Os dois primeiros casos seguem certos; o que
 * faltava era prever este.
 *
 * A extensão é um TERCEIRO DISJUNTO, nunca uma inversão do ramo `confirmed`:
 * inverter quebraria os dialogs de logout e de hard reset, que de fato navegam
 * e desmontam. Quem não passa a flag continua com a truth-table anterior,
 * idêntica.
 *
 * O custo de não ter isto foi medido e é grave, e o pior caso é justamente o
 * caminho de SUCESSO: no webOS o `platformBack()` manda o app pro background e
 * a PÁGINA SOBREVIVE, então o veredito do `useAppExit` é cancelado e o aviso
 * nunca sobe. Sem restore, o usuário sai, relança pelo launcher e encontra as
 * Configurações sem nenhum `[data-focused]` e com as setas mortas — para sempre,
 * até apertar BACK.
 */
export function shouldRestoreFocus(opts: {
  confirmed: boolean
  hideCancel: boolean
  /** O caller declara: depois de confirmar, a tela que abriu este dialog
   *  continua montada? (default: false = o caller navega/desmonta). */
  originStaysMountedOnConfirm?: boolean
}): boolean {
  return !opts.confirmed || opts.hideCancel || opts.originStaysMountedOnConfirm === true
}

interface ConfirmDialogProps {
  title: string
  message: string
  /** Texto do botão de confirmação. Default: `common.confirm` ("Confirmar").
   *  (O docblock dizia `common.ok` e estava errado nas duas pontas: o default
   *  nunca foi essa chave, e `common.ok` valia "Sair"/"Exit"/"Salir", não "OK" —
   *  bomba armada com nome genérico, agora desarmada. Ver o changelog.) */
  confirmLabel?: string
  /** Texto do botão de cancelar. Default: "Cancelar". */
  cancelLabel?: string
  /** Estilo do botão de confirmação. "danger" = vermelho (destrutivo). */
  variant?: 'default' | 'danger'
  /** Esconde o botão Cancelar → dialog vira AVISO de 1 botão. BACK ainda fecha
   *  (via onCancel), pra nunca existir modal sem saída. */
  hideCancel?: boolean
  /**
   * Declare `true` quando confirmar PODE deixar a tela de origem montada — ou
   * seja, quando o `onConfirm` não navega nem recarrega a página, ou faz isso
   * só em ALGUNS caminhos.
   *
   * Três famílias na SettingsScreen, todas medidas:
   *  - "Sair do app": dispara um pedido assíncrono à plataforma e volta pra
   *    mesma tela.
   *  - "Limpar canais/séries/histórico/favoritos": executam e pronto, sem
   *    navegar. Estas são as PIORES sem a flag: não existe aviso posterior que
   *    devolva o foco, então a tela fica órfã até o usuário apertar BACK.
   *  - "Limpar cache" e "recuperar o service worker": recarregam, mas o
   *    `reload()`/`runRecovery()` está DENTRO do try — se a limpeza lançar, a
   *    tela continua viva. Declarar aqui é inócuo no caminho feliz (a página
   *    recarrega de qualquer jeito, depois do restore) e salva o foco no
   *    caminho de erro.
   *
   * Quem NÃO declara: apenas `doLogout` e `doHardReset`, que navegam ou trocam
   * o documento INCONDICIONALMENTE. (`doSwRecovery` declara, e está no grupo do
   * "limpar cache" acima — o `runRecovery` também mora dentro de um try.)
   * Ver `shouldRestoreFocus`.
   */
  originStaysMountedOnConfirm?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  hideCancel = false,
  originStaysMountedOnConfirm = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation()

  // Foco inicial: Cancelar (default seguro pra ação destrutiva) ou, no modo
  // aviso, o único botão existente. Guarda quem tinha o foco pra devolver no
  // fechamento — quem decide se devolve é `shouldRestoreFocus` (acima).
  const restoreKeyRef = useRef<string | null>(null)
  const confirmedRef = useRef(false)
  // Valor CORRENTE de hideCancel pro cleanup: o efeito roda com deps [] (o dialog
  // monta e desmonta como uma unidade), então ler a prop pelo closure congelaria o
  // valor do 1º render.
  const hideCancelRef = useRef(hideCancel)
  hideCancelRef.current = hideCancel
  const originStaysRef = useRef(originStaysMountedOnConfirm)
  originStaysRef.current = originStaysMountedOnConfirm

  /**
   * Devolve o foco pro botão que abriu, guardando o ALVO com
   * `doesFocusableExist`. O guarda é obrigatório e não é decorativo: `setFocus`
   * numa key não registrada BLURA o foco atual, não foca nada e ainda grava a
   * key — some com o `[data-focused]` da tela e mata o D-pad. Se o alvo sumiu
   * (a origem pode ter re-renderizado e escondido o botão), não fazemos nada:
   * deixar o spatial-nav resolver é ruim, matar o foco é pior.
   */
  const restaurarFoco = (): void => {
    const key = restoreKeyRef.current
    if (!key) return
    if (!doesFocusableExist(key)) return
    setFocus(key)
  }

  useEffect(() => {
    const prev = getCurrentFocusKey()
    restoreKeyRef.current =
      prev != null && prev !== CANCEL_KEY && prev !== CONFIRM_KEY ? prev : null
    setFocus(hideCancel ? CONFIRM_KEY : CANCEL_KEY)

    return () => {
      const restore = shouldRestoreFocus({
        confirmed: confirmedRef.current,
        hideCancel: hideCancelRef.current,
        originStaysMountedOnConfirm: originStaysRef.current,
      })
      if (!restore) return
      restaurarFoco()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleConfirm = () => {
    confirmedRef.current = true
    // A JANELA ÓRFÃ FECHA AQUI, não no cleanup.
    //
    // O invariante exigido é "não existe instante entre o OK e o desfecho sem
    // `[data-focused]`". Restaurar só no cleanup do effect não entrega isso: o
    // cleanup é PASSIVO e roda depois do commit que já tirou os botões do
    // dialog do DOM, então sobra uma janela — e o tamanho dela depende da ordem
    // em que o React e os effects passivos do Norigin (o `useFocusable`
    // desregistra num passive effect) resolvem o desmonte. Ancorar num phasing
    // é o erro que esta casa já pagou.
    //
    // Aqui o foco muda ANTES de `onConfirm()` desmontar qualquer coisa, no mesmo
    // tick do OK: o botão de origem ganha `[data-focused]` e o do dialog perde.
    // Não há instante com zero focados, e o resultado não depende de ordem de
    // effect nenhuma. O restore do cleanup continua existindo como REDE (é
    // idempotente: mesma key, segundo `setFocus` é inócuo).
    if (originStaysRef.current) restaurarFoco()
    onConfirm()
  }

  // BACK do remoto/Esc = cancelar (vale também no modo aviso — nenhum modal
  // deste app pode ser inescapável).
  useBackKey(() => {
    onCancel()
  })

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.box}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.message}>{message}</p>
        <div className={styles.actions}>
          {!hideCancel && (
            <Focusable
              focusKey={CANCEL_KEY}
              onEnterPress={onCancel}
              // Toda seta é CONSUMIDA (return false): o foco nunca vaza pra tela
              // atrás do backdrop. Só 'right' tem destino (o botão de confirmar).
              onArrowPress={(dir) => {
                if (dir === 'right') setFocus(CONFIRM_KEY)
                return false
              }}
              className={`${styles.btn} ${styles.btnCancel}`}
            >
              {cancelLabel ?? t('common.cancelAction')}
            </Focusable>
          )}
          <Focusable
            focusKey={CONFIRM_KEY}
            onEnterPress={handleConfirm}
            onArrowPress={(dir) => {
              if (dir === 'left' && !hideCancel) setFocus(CANCEL_KEY)
              return false
            }}
            className={`${styles.btn} ${variant === 'danger' ? styles.btnDanger : styles.btnPrimary}`}
          >
            {confirmLabel ?? t('common.confirm')}
          </Focusable>
        </div>
      </div>
    </div>
  )
}
